﻿<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { CategoryRecord, Id, ImageDraft, ImageMetaRecord, ImportOptions } from './types/catalog'
import { formatBytes } from './utils/format'
import { useCatalog } from './composables/useCatalog'
import { useToast } from './composables/useToast'
import { PRIVACY_SECTIONS } from './utils/privacy'
import AppDialog from './components/AppDialog.vue'
import AppIcon from './components/AppIcon.vue'
import CameraCapture from './components/CameraCapture.vue'
import CategorySelect from './components/CategorySelect.vue'
import CategoryTree from './components/CategoryTree.vue'
import ImageEditorDialog from './components/ImageEditorDialog.vue'
import ImageGallery from './components/ImageGallery.vue'
import ImageImportDialog from './components/ImageImportDialog.vue'
import PwaPrompt from './components/PwaPrompt.vue'
import ToastStack from './components/ToastStack.vue'

const catalog = useCatalog()
const toast = useToast()
const drawerOpen = ref(false)
const importOpen = ref(false)
const cameraOpen = ref(false)
const editorOpen = ref(false)
const editorBusy = ref(false)
const selectedImage = ref<ImageMetaRecord | null>(null)
const pendingFiles = ref<File[]>([])
const searchInput = ref('')
const privacyOpen = ref(false)
const categoryDialogOpen = ref(false)
const categoryDialogMode = ref<'create' | 'rename'>('create')
const categoryDialogName = ref('')
const categoryDialogParentId = ref<Id | null>(null)
const categoryBeingEdited = ref<CategoryRecord | null>(null)
const categoryDialogBusy = ref(false)
let searchTimer = 0

const storagePercent = computed(() => {
  if (!catalog.state.storage.quota) return 0
  return Math.min(100, (catalog.state.storage.usage / catalog.state.storage.quota) * 100)
})

const currentHeading = computed(() => catalog.selectedCategory.value?.name ?? 'All images')
const exportLabel = computed(() => catalog.selectedCategory.value ? 'Export category' : 'Export all')

onMounted(async () => {
  try {
    await catalog.initialize()
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'Failed to start catalog')
  }
})

onBeforeUnmount(() => window.clearTimeout(searchTimer))

watch(searchInput, (value) => {
  window.clearTimeout(searchTimer)
  searchTimer = window.setTimeout(async () => {
    try {
      await catalog.setSearch(value)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Search failed')
    }
  }, 280)
})

async function selectCategory(id: Id | null): Promise<void> {
  drawerOpen.value = false
  try {
    await catalog.selectCategory(id)
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'Failed to load category')
  }
}

function openCreateCategory(parentId: Id | null): void {
  categoryDialogMode.value = 'create'
  categoryDialogName.value = ''
  categoryDialogParentId.value = parentId
  categoryBeingEdited.value = null
  categoryDialogOpen.value = true
}

function openRenameCategory(category: CategoryRecord): void {
  categoryDialogMode.value = 'rename'
  categoryDialogName.value = category.name
  categoryDialogParentId.value = category.parentId
  categoryBeingEdited.value = category
  categoryDialogOpen.value = true
}

async function submitCategory(): Promise<void> {
  categoryDialogBusy.value = true
  try {
    if (categoryDialogMode.value === 'create') {
      const created = await catalog.createCategory(categoryDialogName.value, categoryDialogParentId.value)
      categoryDialogOpen.value = false
      toast.success(`Category “${created.name}” created`)
    } else if (categoryBeingEdited.value) {
      await catalog.renameCategory(categoryBeingEdited.value.id, categoryDialogName.value)
      categoryDialogOpen.value = false
      toast.success('Category renamed')
    }
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'Operation failed')
  } finally {
    categoryDialogBusy.value = false
  }
}

async function removeCategory(category: CategoryRecord): Promise<void> {
  const confirmed = window.confirm(`Delete category “${category.name}”?`)
  if (!confirmed) return
  try {
    await catalog.deleteCategory(category.id)
    toast.success('Category deleted')
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'Deletion failed')
  }
}

function startImport(files: File[] = []): void {
  pendingFiles.value = files
  importOpen.value = true
}

async function submitImport(files: File[], options: ImportOptions): Promise<void> {
  try {
    const result = await catalog.addImages(files, options, files.length === 1 && files[0].name.startsWith('foto-') ? Date.now() : undefined)
    if (result.failed.length) {
      toast.error(`${result.imported} images imported; ${result.failed.length} failed. First error: ${result.failed[0].error}`)
    } else {
      toast.success(`${result.imported} ${result.imported === 1 ? 'image imported' : 'images imported'}`)
    }
    if (result.imported > 0) importOpen.value = false
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'Import failed')
  }
}

function captured(file: File): void {
  cameraOpen.value = false
  startImport([file])
}

function openEditor(image: ImageMetaRecord): void {
  selectedImage.value = image
  editorOpen.value = true
}

async function saveImage(draft: ImageDraft): Promise<void> {
  if (!selectedImage.value) return
  editorBusy.value = true
  try {
    await catalog.updateImage(selectedImage.value.id, draft)
    editorOpen.value = false
    selectedImage.value = null
    toast.success('Image updated')
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'Save failed')
  } finally {
    editorBusy.value = false
  }
}

async function rotateSelected(degrees: 90 | -90 | 180): Promise<void> {
  if (!selectedImage.value) return
  editorBusy.value = true
  try {
    await catalog.rotateImage(selectedImage.value.id, degrees)
    selectedImage.value = catalog.state.images.find((image) => image.id === selectedImage.value?.id) ?? selectedImage.value
    toast.success('Image rotated')
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'Rotation failed')
  } finally {
    editorBusy.value = false
  }
}

async function removeImage(image = selectedImage.value): Promise<void> {
  if (!image) return
  const confirmed = window.confirm(`Permanently delete “${image.title}”?`)
  if (!confirmed) return
  editorBusy.value = true
  try {
    await catalog.deleteImage(image.id)
    editorOpen.value = false
    selectedImage.value = null
    toast.success('Image deleted')
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'Deletion failed')
  } finally {
    editorBusy.value = false
  }
}

async function toggleDescendants(event: Event): Promise<void> {
  try {
    await catalog.setIncludeDescendants((event.target as HTMLInputElement).checked)
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'Filter failed')
  }
}

async function exportZip(): Promise<void> {
  try {
    await catalog.exportZip(catalog.state.selectedCategoryId)
    toast.success('ZIP archive created')
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'Export failed')
  }
}

</script>

<template>
  <div class="app-shell">
    <header class="topbar">
      <button type="button" class="icon-button topbar-menu" aria-label="Open categories" @click="drawerOpen = true">
        <AppIcon name="menu" />
      </button>
      <div class="brand">
        <div class="brand-mark"><AppIcon name="camera" :size="23" /></div>
        <div class="brand-copy"><strong>Pictalog</strong><span>Capture, tag and organize your images.</span></div>
      </div>
      <label class="search-box">
        <AppIcon name="search" :size="19" />
        <input v-model="searchInput" type="search" placeholder="Search by tag or description" autocomplete="off" />
        <button v-if="searchInput" type="button" class="search-clear" aria-label="Clear search" @click="searchInput = ''">
          <AppIcon name="close" :size="15" />
        </button>
      </label>
      <div class="topbar-stats" :title="`${formatBytes(catalog.state.storage.usage)} used of ${formatBytes(catalog.state.storage.quota)}`">
        <AppIcon name="storage" :size="19" />
        <div><strong>{{ catalog.state.totalImages }}</strong><span>images</span></div>
      </div>
    </header>

    <aside class="sidebar">
      <CategoryTree
        :categories="catalog.state.categories"
        :selected-id="catalog.state.selectedCategoryId"
        @select="selectCategory"
        @add="openCreateCategory"
        @rename="openRenameCategory"
        @remove="removeCategory"
      />
      <div style="width: 160px; height: 300px; background-color: cornflowerblue; margin-top: 20px; margin-bottom: 20px; align-self: anchor-center;">
        <a href="https://partner.pcloud.com/r/156832">
          <img style="width:160px; height: 300px;" src="/images/banner_pcloud.png" />
        </a>
      </div>

      <div class="storage-card">
        <div class="storage-card-heading">
          <AppIcon name="storage" :size="18" />
          <span>Local storage</span>
          <strong>{{ storagePercent.toFixed(0) }}%</strong>
        </div>
        <div class="storage-bar"><span :style="{ width: `${storagePercent}%` }" /></div>
        <p>{{ formatBytes(catalog.state.storage.usage) }} di {{ formatBytes(catalog.state.storage.quota) }}</p>
        <small>{{ catalog.state.storage.persisted ? 'Persistent storage active' : 'The browser may free space if needed' }}</small>
      </div>
      <button type="button" class="sidebar-privacy-link" @click="privacyOpen = true">
        <AppIcon name="info" :size="15" /> Privacy Policy
      </button>
    </aside>

    <Transition name="drawer-fade">
      <div v-if="drawerOpen" class="mobile-drawer-backdrop" @click.self="drawerOpen = false">
        <aside class="mobile-drawer">
          <div class="mobile-drawer-header">
            <div class="brand"><div class="brand-mark"><AppIcon name="camera" :size="21" /></div><strong>Pictalog</strong></div>
            <button type="button" class="icon-button" aria-label="Close categories" @click="drawerOpen = false"><AppIcon name="close" /></button>
          </div>
          <CategoryTree
            :categories="catalog.state.categories"
            :selected-id="catalog.state.selectedCategoryId"
            @select="selectCategory"
            @add="openCreateCategory"
            @rename="openRenameCategory"
            @remove="removeCategory"
          />

          <div style="width: 300px; height: 250px; background-color: cornflowerblue; margin-top: 20px; margin-bottom: 20px; margin-left: 0px; align-self: anchor-center;">
            <a href="https://partner.pcloud.com/r/156832">
              <img style="width:300px; height: 250px;" src="/images/banner_mobile.png" />
            </a>
          </div>

          <button type="button" class="mobile-drawer-privacy-link" @click="privacyOpen = true">
            <AppIcon name="info" :size="15" /> Privacy Policy
          </button>
        </aside>
      </div>
    </Transition>

    <main class="main-content">
      <section class="content-header">
        <div class="heading-area">
          <div class="breadcrumbs">
            <button type="button" @click="selectCategory(null)">Catalog</button>
            <template v-for="crumb in catalog.selectedBreadcrumbs.value" :key="crumb.id">
              <AppIcon name="chevron" :size="13" />
              <button type="button" @click="selectCategory(crumb.id)">{{ crumb.name }}</button>
            </template>
          </div>
          <div class="page-title-row">
            <div>
              <h1>{{ currentHeading }}</h1>
              <p>
                {{ catalog.state.images.length }} shown<span v-if="catalog.state.hasMore">, more available</span>
                <span v-if="catalog.state.search"> · results for “{{ catalog.state.search }}”</span>
              </p>
            </div>
            <label v-if="catalog.state.selectedCategoryId" class="switch-control">
              <input :checked="catalog.state.includeDescendants" type="checkbox" @change="toggleDescendants" />
              <span class="switch-track" />
              <span>Include subcategories</span>
            </label>
          </div>
        </div>

        <div class="primary-actions">
          <button type="button" class="button button--secondary" @click="cameraOpen = true">
            <AppIcon name="camera" :size="18" /> Camera
          </button>
          <button type="button" class="button button--primary" @click="startImport()">
            <AppIcon name="upload" :size="18" /> Import
          </button>
          <button type="button" class="button button--secondary" :disabled="catalog.state.exporting || !catalog.state.totalImages" @click="exportZip">
            <span v-if="catalog.state.exporting" class="spinner spinner--small" />
            <AppIcon v-else name="download" :size="18" /> {{ catalog.state.exporting ? 'Exporting…' : exportLabel }}
          </button>
        </div>
      </section>

      <div v-if="catalog.state.exporting && catalog.state.exportProgress" class="export-progress-banner">
        <AppIcon name="download" :size="18" />
        <div>
          <strong>Creating archive {{ catalog.state.exportProgress.current }} / {{ catalog.state.exportProgress.total }}</strong>
          <span>{{ catalog.state.exportProgress.fileName }}</span>
        </div>
      </div>

      <ImageGallery
        :images="catalog.state.images"
        :loading="catalog.state.loadingImages"
        :has-more="catalog.state.hasMore"
        @load-more="catalog.loadMoreImages"
        @edit="openEditor"
        @remove="removeImage"
      />
    </main>

    <nav class="mobile-action-bar" aria-label="Main actions">
      <button type="button" @click="drawerOpen = true"><AppIcon name="folder" /><span>Categories</span></button>
      <button type="button" @click="cameraOpen = true"><AppIcon name="camera" /><span>Camera</span></button>
      <button type="button" class="mobile-action-primary" @click="startImport()"><AppIcon name="plus" :size="27" /><span>Import</span></button>
      <button type="button" :disabled="catalog.state.exporting" @click="exportZip"><AppIcon name="download" /><span>Export</span></button>
    </nav>

    <ImageImportDialog
      :open="importOpen"
      :categories="catalog.state.categories"
      :default-category-id="catalog.state.selectedCategoryId"
      :initial-files="pendingFiles"
      :importing="catalog.state.importing"
      :progress="catalog.state.importProgress"
      @close="importOpen = false"
      @submit="submitImport"
    />

    <CameraCapture :open="cameraOpen" @close="cameraOpen = false" @captured="captured" />

    <ImageEditorDialog
      :open="editorOpen"
      :image="selectedImage"
      :categories="catalog.state.categories"
      :busy="editorBusy"
      @close="editorOpen = false"
      @save="saveImage"
      @remove="removeImage()"
      @rotate="rotateSelected"
    />

    <AppDialog
      :open="categoryDialogOpen"
      :title="categoryDialogMode === 'create' ? 'New category' : 'Rename category'"
      :close-disabled="categoryDialogBusy"
      @close="categoryDialogOpen = false"
    >
      <template #eyebrow>Organization</template>
      <form class="category-dialog-form" @submit.prevent="submitCategory">
        <label class="field">
          <span>Name</span>
          <input v-model="categoryDialogName" type="text" maxlength="80" autofocus required placeholder="e.g. Documents, Travel, Work" />
        </label>
        <CategorySelect
          v-if="categoryDialogMode === 'create'"
          v-model="categoryDialogParentId"
          :categories="catalog.state.categories"
          label="Parent category"
          empty-label="None (root level)"
        />
      </form>
      <template #footer>
        <button type="button" class="button button--ghost" :disabled="categoryDialogBusy" @click="categoryDialogOpen = false">Cancel</button>
        <button type="button" class="button button--primary" :disabled="categoryDialogBusy || !categoryDialogName.trim()" @click="submitCategory">
          <span v-if="categoryDialogBusy" class="spinner spinner--small" />
          <AppIcon v-else name="check" :size="18" /> Save
        </button>
      </template>
    </AppDialog>

    <ToastStack />
    <PwaPrompt />

    <AppDialog :open="privacyOpen" title="Privacy Policy" wide @close="privacyOpen = false">
      <template #eyebrow>Pictalog</template>
      <div class="privacy-content">
        <p class="privacy-intro">
          <strong>Pictalog</strong> is an offline-first photo catalog. Your data stays on your device and is never sent to our servers or shared with third parties.
        </p>
        <article v-for="section in PRIVACY_SECTIONS" :key="section.title" class="privacy-section">
          <h3>{{ section.title }}</h3>
          <p>{{ section.body }}</p>
        </article>
      </div>
      <template #footer>
        <span class="dialog-footer-spacer" />
        <button type="button" class="button button--primary" @click="privacyOpen = false">Close</button>
      </template>
    </AppDialog>
  </div>
</template>
